import React, { useCallback, useMemo, useRef, useContext } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import { makeGetMilestone, makeGetSortedActiveProjectMilestones } from '../selectors/milestones';
import { makeGetSortedActiveProjectTaskGroups } from '../selectors/task-groups';
import { makeGetSortedMilestoneActiveTasks, makeGetMilestoneCompletedTasks } from '../selectors/tasks';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { updateMilestone, removeMilestone } from '../actions/milestones';
import { fetchTasks } from '../actions/tasks';

import { trackEvent } from '../services/events-tracker-service';
import { getMilestoneProgress, getMilestoneType } from '../utils/milestone';
import { createTask } from '../actions/tasks';

import { TasksDNDContext } from 'context/TasksDNDContext';

import MilestoneOverviewGroup from './milestone-overview-group';
import MilestoneOverviewHead from './milestone-overview-head';

import TaskGroupPlaceholder from './placeholders/task-group.placeholder';

const MilestoneOverview = ({ id, projectId, milestones, milestone, onMilestoneClick, onCollapse, onExpand, collapsed, taskGroupsLoading, taskGroups, tasks, completedTasks, onTaskClick, t }) => {
    const tasksDNDContext = useContext(TasksDNDContext);

    const handleTaskCreate = useCallback((taskGroupId, name) => {
        createTask({
            project: projectId,
            task_group: taskGroupId,
            name: name,
            milestone: id
        }).then(() => {
            trackEvent('TASK.CREATE', {
                via: 'Milestone Overview'
            });
        });
    }, [projectId, id]);

    const updateDependentMilestones = useCallback((milestoneId, diff) => {
        if (!diff) {
            return;
        }

        milestones
            .forEach(milestone => {
                if (milestone.dependency === milestoneId) {
                    update(milestone.id, {
                        start: new Date(milestone.start).getTime() + diff,
                        end: new Date(milestone.end).getTime() + diff
                    });
                }
            });
    }, [milestones]);

    const update = useCallback((milestoneId, milestoneProps) => {
        if (milestoneProps.end) {
            const milestone = milestones.find(milestone => milestone.id === milestoneId);

            updateDependentMilestones(milestoneId, milestoneProps.end - milestone.end);
        }

        updateMilestone(id, milestoneProps);
    }, [milestones, updateDependentMilestones]);

    const handleEdit = useCallback(milestoneProps => {
        update(id, milestoneProps);
    }, [update, id]);

    const handleDelete = useCallback(() => {
        trackEvent('MILESTONE.REMOVE');
        removeMilestone(id);
    }, [id]);

    const getMilestoneTasks = useCallback((milestoneTasks, taskGroupId) => {
        return milestoneTasks.filter(task => task.task_group === taskGroupId);
    }, []);

    const duration = useMemo(() => {
        return moment(milestone.end).diff(milestone.start, 'days');
    }, [milestone]);

    const progress = useMemo(() => {
        return getMilestoneProgress(milestone);
    }, [milestone]);

    const type = useMemo(() => {
        return getMilestoneType(milestone);
    }, [milestone]);

    return (
        <div className={'milestone-overview ' + type}>
            <MilestoneOverviewHead
                id={id}
                duration={duration}
                progress={progress}
                type={type}
                milestones={milestones}
                start={milestone.start}
                end={milestone.end}
                collapsed={collapsed}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMilestoneClick={onMilestoneClick}
                onCollapse={onCollapse}
                onExpand={onExpand}
            />

            {
                !collapsed ? (
                    <React.Fragment>
                        {
                            taskGroupsLoading ? (
                                <div className="overview-task-groups">
                                    <div className="overview-group" key="1">
                                        <div className="overview-group-list">
                                            <TaskGroupPlaceholder />
                                        </div>
                                    </div>

                                    <div className="overview-group" key="2">
                                        <div className="overview-group-list">
                                            <TaskGroupPlaceholder />
                                        </div>
                                    </div>

                                    <div className="overview-group" key="3">
                                        <div className="overview-group-list">
                                            <TaskGroupPlaceholder />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="overview-task-groups">
                                    {
                                        taskGroups.map(taskGroup => <MilestoneOverviewGroup
                                            key={taskGroup.id}
                                            milestoneId={id}
                                            taskGroupId={taskGroup.id}
                                            name={taskGroup.name}
                                            tasks={getMilestoneTasks(tasks, taskGroup.id).map(task => task.id)}
                                            draggedTaskId={tasksDNDContext.draggedId}
                                            onCreateTask={handleTaskCreate}
                                            onTaskClick={onTaskClick}
                                            onTaskDragStart={tasksDNDContext.onDragStart}
                                            onTaskDragEnd={tasksDNDContext.onDragEnd}
                                            onTaskMove={tasksDNDContext.onMove}
                                        />)
                                    }

                                    <MilestoneOverviewGroup
                                        key="completed"
                                        milestoneId={id}
                                        taskGroupId="completed"
                                        name={ t('Completed') }
                                        tasks={completedTasks.map(task => task.id)}
                                        draggedTaskId={tasksDNDContext.draggedId}
                                        onTaskClick={onTaskClick}
                                        onTaskDragStart={tasksDNDContext.onDragStart}
                                        onTaskDragEnd={tasksDNDContext.onDragEnd}
                                        onTaskMove={tasksDNDContext.onMove}
                                    />
                                </div>
                            )
                        }
                    </React.Fragment>
                ) : null
            }
        </div>
    );
}


const makeMapStateToProps = () => {
    const getMilestone = makeGetMilestone();
    const getSortedActiveProjectTaskGroups = makeGetSortedActiveProjectTaskGroups();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getTaskGroupsLoading = makeGetLoadingState();
    const getSortedMilestoneActiveTasks = makeGetSortedMilestoneActiveTasks();
    const getTasksFetched = makeGetFetchedState();
    const getTasksLoading = makeGetLoadingState();
    const getMilestoneCompletedTasks = makeGetMilestoneCompletedTasks();

    return ({ users, tasks, taskGroups, milestones }, ownProps) => {
        const milestone = getMilestone(milestones.items, ownProps.id);
        const milestoneTasks = getSortedMilestoneActiveTasks(tasks.items, ownProps.id);

        return {
            milestone,
            milestones: getSortedActiveProjectMilestones(milestones.items, ownProps.projectId),
            taskGroups: getSortedActiveProjectTaskGroups(taskGroups.items, ownProps.projectId),
            taskGroupsLoading: getTaskGroupsLoading(taskGroups.fetchInfo, 'project', ownProps.projectId),
            tasks: milestoneTasks,
            completedTasks: getMilestoneCompletedTasks(tasks.items, ownProps.id),
            tasksLoading: getTasksLoading(tasks.fetchInfo, 'milestone', ownProps.id),
            tasksFetched: getTasksFetched(tasks.fetchInfo, 'milestone', ownProps.id)
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation('common')(MilestoneOverview));
